/** Witness statement wizard (Dean, 14 Oct 2026) — Set up → Exhibits → Draft → Review → Finish. One wizard; the stage picks the heading,
 *  the legal framework (CPR 32 / IA 1986 s.268 + IR 2016 r.10.5 / CPR 15-16) and the block library. Drafts autosave; GPT-5.5 drafts from the file. */
import { desktopOnly } from "@/components/phone/DesktopOnlyNote";
import { useCallback, useEffect, useRef, useState } from "react";
import { Check, FileSignature, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { createWitnessStatement, getWitnessAiRun, getWitnessContext, getWitnessStatement, startWitnessAi, updateWitnessStatement } from "@/lib/api";
import { WitnessSetup } from "./WitnessSetup";
import { WitnessExhibits } from "./WitnessExhibits";
import { WitnessDraft } from "./WitnessDraft";
import { WitnessReview } from "./WitnessReview";
import { WitnessFinish } from "./WitnessFinish";
import { STEPS, errMsg, fmtWhen, placeholdersOf } from "./witnessShared";

const today = () => new Date().toISOString().slice(0, 10);

function StepBar({ step, setStep, enabled, final }) {
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="ws-steps">
      {STEPS.map((s, i) => {
        const idx = STEPS.findIndex((x) => x.key === step);
        const done = i < idx;
        return (
          <button key={s.key} type="button" disabled={!enabled && s.key !== "setup"} onClick={() => setStep(s.key)} data-testid={`ws-step-${s.key}`} aria-current={step === s.key}
            className={cn("flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] border transition-colors", step === s.key ? "bg-slate-900 text-white border-slate-900" : done ? "bg-slate-100 border-slate-200 text-slate-700" : "border-slate-200 text-slate-500 hover:bg-slate-50", !enabled && s.key !== "setup" && "opacity-50")}>
            <span className="font-mono">{done ? <Check className="w-3 h-3" /> : i + 1}</span>{s.label}{s.key === "finish" && final && <Lock className="w-3 h-3" />}
          </button>
        );
      })}
    </div>
  );
}

function WitnessStatementDialog({ caseRow, onClose, presetStage, onDone }) {
  const [ctx, setCtx] = useState(null);
  const [ws, setWs] = useState(null);
  const [step, setStep] = useState("setup");
  const [dated, setDated] = useState(today());
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [progress, setProgress] = useState("");
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const pending = useRef({});
  const timer = useRef(null);
  const wsRef = useRef(null);
  wsRef.current = ws;

  const loadCtx = useCallback(async () => {
    try { setCtx(await getWitnessContext(caseRow.case_id)); } catch (e) { toast.error(errMsg(e)); onClose(); }
  }, [caseRow.case_id, onClose]);
  useEffect(() => { loadCtx(); }, [loadCtx]);

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    const patch = pending.current;
    pending.current = {};
    const cur = wsRef.current;
    if (!cur || !Object.keys(patch).length || cur.status === "final") return;
    setSaving(true);
    try {
      const r = await updateWitnessStatement(caseRow.case_id, cur.id, patch);
      // keep what the author typed since; take the server's derived fields (exhibits cited, versions, number)
      setWs((w) => (!w || w.id !== r.id ? w : { ...w, versions: r.versions, number: r.number, updated_at: r.updated_at,
        paragraphs: (w.paragraphs || []).map((p) => { const s = (r.paragraphs || []).find((x) => x.id === p.id); return s ? { ...p, exhibits: s.exhibits, text: s.text } : p; }) }));
      setSavedAt(r.updated_at);
    } catch (e) { toast.error(`Not saved — ${errMsg(e)}`); } finally { setSaving(false); }
  }, [caseRow.case_id]);

  const patch = useCallback((partial) => {
    setWs((w) => ({ ...w, ...partial }));
    pending.current = { ...pending.current, ...partial };
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 900);
  }, [flush]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const go = async (k) => { await flush(); setStep(k); };

  /** Generate with AI (Set up): create the draft, run GPT-5.5 with apply=true (the backend writes the result into the draft even if
   *  this dialog is closed), poll, then land on Exhibits with the AI's exhibits already marked. */
  const generate = async ({ stage, deponent, instructions }) => {
    setProgress("Creating the draft…");
    let w;
    try {
      w = await createWitnessStatement(caseRow.case_id, { stage, deponent, block_ids: [] });
      const run = await startWitnessAi(caseRow.case_id, w.id, { mode: "draft", instructions, apply: true });
      const started = Date.now();
      let r = run;
      while (r.status === "running") {
        await new Promise((res) => setTimeout(res, 3000));
        if (!alive.current) return;
        r = await getWitnessAiRun(caseRow.case_id, w.id, run.run_id);
        setProgress(`${r.stage} · ${Math.round((Date.now() - started) / 1000)}s`);
      }
      if (r.status === "failed") throw new Error(r.error || "The AI run failed");
      const fresh = await getWitnessStatement(caseRow.case_id, w.id);
      setWs(fresh); setSavedAt(fresh.updated_at); setStep("exhibits"); loadCtx();
      const n = (fresh.paragraphs || []).filter((p) => p.kind !== "heading").length;
      const ex = (fresh.exhibits || []).length;
      toast.success(`Draft ready — ${n} paragraphs${ex ? `, ${ex} exhibit${ex === 1 ? "" : "s"} marked` : ""}. Check the exhibits, then review every paragraph.`);
    } catch (e) {
      toast.error(errMsg(e));
      if (w) { try { const fresh = await getWitnessStatement(caseRow.case_id, w.id); setWs(fresh); setStep("draft"); } catch { /* draft may be gone */ } }
      loadCtx();
    } finally { if (alive.current) setProgress(""); }
  };
  const open = async (id) => {
    try { const w = await getWitnessStatement(caseRow.case_id, id); setWs(w); setStep(w.status === "final" ? "finish" : "exhibits"); if (w.final?.dated) setDated(w.final.dated); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const close = async () => { if (progress && !window.confirm("The AI is still writing — the draft will finish in the background and appear under 'Statements on this file'. Close anyway?")) return; await flush(); onClose(); onDone?.(); };
  const final = ws?.status === "final";
  const stageLabel = ctx && ws ? ctx.stages.find((s) => s.key === ws.stage)?.label : "";

  return (
    <Dialog open onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-6xl max-h-[94vh] overflow-y-auto" aria-describedby={undefined} data-testid="ws-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2 flex-wrap">
            <FileSignature className="w-4 h-4 text-rose-600" />Witness statement
            <span className="text-[11px] font-normal text-muted-foreground">{caseRow.loan_application_id} · {caseRow.customer_name}{ws ? ` · statement ${ws.number || 1} of ${ws.deponent?.name} · ${stageLabel}` : ""}</span>
            <span className="flex-1" />
            {ws && !final && placeholdersOf(ws.paragraphs).length > 0 && <span className="text-[10px] font-normal text-amber-800 bg-yellow-100 border border-yellow-300 rounded px-1.5" data-testid="ws-placeholder-count">{placeholdersOf(ws.paragraphs).length} placeholder{placeholdersOf(ws.paragraphs).length === 1 ? "" : "s"} to replace</span>}
            {ws && !final && <span className="text-[10px] font-normal text-slate-500" data-testid="ws-save-state">{saving ? "Saving…" : savedAt ? `Saved ${fmtWhen(savedAt)}` : "Draft"}</span>}
            {final && <span className="text-[10px] font-normal text-emerald-700 flex items-center gap-1" data-testid="ws-final-badge"><Lock className="w-3 h-3" /> final</span>}
          </DialogTitle>
        </DialogHeader>
        <StepBar step={step} setStep={go} enabled={!!ws} final={final} />
        {!ctx ? <div className="py-10 flex items-center justify-center text-xs text-slate-500" data-testid="ws-loading"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading the file…</div> : (
          <div className="pt-1">
            {step === "setup" && <WitnessSetup caseRow={caseRow} ctx={ctx} presetStage={presetStage} onOpen={open} onListChanged={loadCtx} onGenerate={generate} progress={progress} onCreated={(w) => { setWs(w); setSavedAt(w.updated_at); setStep("exhibits"); loadCtx(); }} />}
            {step === "exhibits" && ws && <WitnessExhibits ws={ws} ctx={ctx} onPatch={patch} disabled={final} />}
            {step === "draft" && ws && <WitnessDraft caseRow={caseRow} ctx={ctx} ws={ws} onPatch={patch} onFlush={flush} onReplaceWs={(w) => { pending.current = {}; setWs(w); setSavedAt(w.updated_at); }} disabled={final} />}
            {step === "review" && ws && <WitnessReview caseRow={caseRow} ws={ws} onPatch={patch} dated={dated} setDated={setDated} disabled={final} savedAt={savedAt} />}
            {step === "finish" && ws && <WitnessFinish caseRow={caseRow} ws={ws} ctx={ctx} dated={dated} onGoTo={go} onFinished={(w) => { setWs(w); loadCtx(); }} />}
          </div>
        )}
        {ws && (
          <div className="flex items-center justify-between pt-2 border-t">
            <Button variant="ghost" size="sm" onClick={close} data-testid="ws-close">Close</Button>
            <div className="flex gap-2">
              {STEPS.findIndex((s) => s.key === step) > 0 && <Button variant="outline" size="sm" onClick={() => go(STEPS[STEPS.findIndex((s) => s.key === step) - 1].key)} data-testid="ws-back">← Back</Button>}
              {STEPS.findIndex((s) => s.key === step) < STEPS.length - 1 && step !== "setup" && <Button size="sm" onClick={() => go(STEPS[STEPS.findIndex((s) => s.key === step) + 1].key)} data-testid="ws-next">Next →</Button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default desktopOnly(WitnessStatementDialog, "The witness statement wizard");
